import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { canAddProductToCart } from "~/domains/product/utils/canAddProductToCart";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useRegularPnaProductDescriptors } from "~/domains/pna/useRegularPnaProductDescriptors";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { getFirstValidPackCode } from "~/features/wishlist/components/modals/AddToWishlist/utils";
import { selectSelectedWishlistEntries } from "~/features/wishlist/redux/wishlistSelectors";
import type { ExpandedResolvedWishlistEntryModel } from "~/features/wishlist/types/model";
import { normalizeWishlistPriceReference } from "~/features/wishlist/utils/priceReference";

export interface WishlistEntriesWithPna {
  entries: ExpandedResolvedWishlistEntryModel[];
  /** addAllClicked && some entry PnA still in flight — the "fetching in progress" ATC banner. */
  showATCNotReadyMsg: boolean;
  /** addAllClicked && some entry PnA failed — the wsError ATC banner. */
  showWSErrorMsg: boolean;
}

/**
 * Per-entry wishlist descriptors from the canonical PnA queries (A05 wishlist slice).
 *
 * Basic products are staged by `getWishlists` into `products.basic` (sanctioned base source);
 * price queries are keyed per (productCode, priceReference) pair, availability per productCode.
 * Canonical options disable CONFIGURATOR entries (the legacy `selectProductDescriptor` mapping
 * for configurators). Replaces the `selectFullWishlist` dictionary reads and the
 * `selectShowATCNotReadyMsg` / `selectShowWSErrorMsg` selector banner derivations.
 */
export function useWishlistEntriesWithPna(): WishlistEntriesWithPna {
  const wishlistEntries = useSelector(selectSelectedWishlistEntries, shallowEqual);
  const entryUIState = useSelector((state: RootState) => state.wishlistUI.entryUIState);
  const addAllClicked = useSelector((state: RootState) => state.wishlistUI.addAllClicked);
  const bases = useSelector(
    (state: RootState) => wishlistEntries.map((entry) => state.products.basic[entry.productCode]),
    shallowEqual,
  );

  const descriptorRequests = useMemo(
    () =>
      wishlistEntries.map((entry, index) => ({
        storefrontCode: entry.productCode,
        base: bases[index],
        priceReference: normalizeWishlistPriceReference(entry.priceReference),
      })),
    [bases, wishlistEntries],
  );
  const descriptors = useRegularPnaProductDescriptors(descriptorRequests);

  const entries = useMemo(
    () =>
      wishlistEntries.map((entry, index) => {
        const product: ProductDescriptor = descriptors[index];

        return {
          ...entry,
          product,
          packCode: getFirstValidPackCode(product),
          isSaleEnabled: entryUIState[entry.id]?.isSaleEnabled ?? false,
          isSelectable: userIsRegistered()
            ? true
            : product.isLoaded && canAddProductToCart(product, product?.availability?.packagingInformation[0]),
        };
      }),
    [descriptors, entryUIState, wishlistEntries],
  );

  return {
    entries,
    showATCNotReadyMsg: addAllClicked && entries.some((entry) => entry.product.isLoading),
    showWSErrorMsg: addAllClicked && entries.some((entry) => entry.product.isError),
  };
}
